/**
 * Where the CNC dialog keeps machine profiles: the WORKSPACE's shared list on
 * the Vault server when the user is signed into one, else THIS BROWSER's
 * localStorage shelf — which is also the fallback when the server cannot be
 * reached, and the only store on `/dev/ring` (no session there, so it keeps
 * working exactly as before the server store existed).
 *
 * Pure: the rules for turning one store's shape into the dialog's
 * `CncProfileShelf` and back. The React side is `useCncProfiles`.
 *
 * The two shapes differ in one way that matters. The browser shelf remembers
 * which profile is SELECTED; the workspace list has a DEFAULT, shared by every
 * member, that only an editor may change. Picking a profile in the dialog is
 * therefore this user's choice, remembered per workspace in this browser, and
 * never a save — a member who cannot edit can still pick.
 */
import type { CncMachineProfile } from "../../../shared/api-contract";
import { validateCncProfile } from "./cncProfile";
import type { CncProfileShelf, KeyValueStore } from "./cncProfile";

/** What the server sends (`WorkspaceCncProfiles`), the part these rules read. */
export interface WorkspaceProfileList {
  profiles: CncMachineProfile[];
  defaultProfileId: string | null;
  invalid: { name: string; errors: string[] }[];
}

/**
 * The workspace's list as a shelf: every profile that passes today's
 * `validateCncProfile` (the server already lists the others in `invalid`, and
 * the viewer would refuse them), opened on the profile this user last picked
 * in this workspace, else the workspace default, else the first. Null when no
 * profile passes. `left` names what was left out, and why.
 */
export function shelfFromWorkspace(
  list: WorkspaceProfileList,
  remembered: string | null,
): { shelf: CncProfileShelf | null; left: { name: string; errors: string[] }[] } {
  const profiles: CncMachineProfile[] = [];
  const left = [...list.invalid];
  for (const p of list.profiles) {
    const check = validateCncProfile(p);
    if (!check.ok) {
      if (!left.some((l) => l.name === p.name)) left.push({ name: String(p?.name ?? "(no name)"), errors: check.errors });
    } else if (!profiles.some((q) => q.name === check.profile.name)) {
      profiles.push(check.profile);
    }
  }
  if (profiles.length === 0) return { shelf: null, left };
  const has = (n: string | null): n is string => !!n && profiles.some((p) => p.name === n);
  const selected = has(remembered) ? remembered : has(list.defaultProfileId) ? list.defaultProfileId : profiles[0].name;
  return { shelf: { selected, profiles }, left };
}

/**
 * The workspace HAS stored profiles, but none of them passes today's checks:
 * `shelfFromWorkspace` gave no shelf, yet the list is not empty. Not the same
 * as an empty workspace — saving a seeded shelf over it would DELETE what is
 * stored, so the dialog must neither call it empty nor save over it without an
 * explicit confirm.
 */
export function storedButUnusable(list: WorkspaceProfileList, read: { shelf: CncProfileShelf | null }): boolean {
  return !read.shelf && (list.profiles.length > 0 || list.invalid.length > 0);
}

/** True when `next` differs from `prev` only in which profile is selected. */
export function onlySelectionChanged(prev: CncProfileShelf, next: CncProfileShelf): boolean {
  return prev.profiles === next.profiles
    || (prev.profiles.length === next.profiles.length && prev.profiles.every((p, i) => p === next.profiles[i]));
}

/**
 * The workspace default after an edit from `prev` to `next`. A profile's NAME
 * is its identity on the server, so a rename must carry the default with it in
 * the same save (the server refuses a default that names no profile):
 *   kept      the default's name is still on the shelf;
 *   renamed   the list is the same length and the default's slot now holds
 *             another name (the editor renames in place);
 *   cleared   the default was deleted.
 */
export function remapDefault(prev: CncProfileShelf, next: CncProfileShelf, current: string | null): string | null {
  if (!current) return null;
  if (next.profiles.some((p) => p.name === current)) return current;
  const at = prev.profiles.findIndex((p) => p.name === current);
  if (at >= 0 && prev.profiles.length === next.profiles.length) return next.profiles[at].name;
  return null;
}

/** The PUT body for a shelf (`SaveWorkspaceCncProfiles`). */
export function workspaceSaveBody(shelf: CncProfileShelf, defaultName: string | null, expectedUpdatedAt: string | null) {
  return {
    profiles: shelf.profiles,
    defaultProfileId: defaultName && shelf.profiles.some((p) => p.name === defaultName) ? defaultName : null,
    expectedUpdatedAt,
  };
}

// ── This user's pick, per workspace ────────────────────────────────────────

export const CNC_WORKSPACE_PICK_KEY = "ringBuilder.cnc.workspacePick.v1";

function readPicks(store: KeyValueStore | null): Record<string, string> {
  try {
    const raw = store?.getItem(CNC_WORKSPACE_PICK_KEY);
    const v: unknown = raw ? JSON.parse(raw) : null;
    if (!v || typeof v !== "object" || Array.isArray(v)) return {};
    return Object.fromEntries(Object.entries(v).filter((e): e is [string, string] => typeof e[1] === "string"));
  } catch {
    return {};
  }
}

/** The profile this user last picked in `workspaceId`, in this browser. */
export function loadWorkspacePick(workspaceId: string, store: KeyValueStore | null): string | null {
  return readPicks(store)[workspaceId] ?? null;
}

export function saveWorkspacePick(workspaceId: string, name: string, store: KeyValueStore | null): void {
  try {
    store?.setItem(CNC_WORKSPACE_PICK_KEY, JSON.stringify({ ...readPicks(store), [workspaceId]: name }));
  } catch {
    /* blocked storage: the pick is simply not remembered */
  }
}
